import {
	dateToUTCString,
	formatDate,
	isAfter,
	isBefore,
	isSame,
	localOrUtcTimestampToLocalDate,
	startOf,
} from "@sembark-travel/datetime-utils"
import { TSearchParams } from "@sembark-travel/ui-list"
import { useCallback, useEffect, useMemo, useReducer } from "react"
import { IHotelGroup } from "../HotelGroups"
import { IHotel } from "../Hotels"
import { ILocation } from "../Locations"
import Storage from "../storage"
import { TTripDestination } from "../TripDestinations"
import { ITripSource } from "../TripSources"
import { IUser } from "../Users"
import { IBooking, TBookingSlot } from "./store"

export type TBookingStatus =
	| "new"
	| "in_progress"
	| "booked"
	| "on_trip"
	| "past"
	| "dropped"
	| "all"
type TPaxDisplayMode = "full" | "short" | "compact"

export interface IFilters extends TSearchParams {
	booking_tags?: Array<{ id: number; name: string }>
	booking_stages?: Array<{ id: string | number; name: string }>
	payment_statuses?: Array<{ id: string | number; name: string }>
	trip_sources?: Array<ITripSource>
	owners?: Array<IUser>
	trip_owners?: Array<IUser>
	locations?: Array<ILocation>
	trip_destinations?: Array<TTripDestination>
	hotels?: Array<IHotel>
	hotel_groups?: Array<IHotelGroup>
	status?: TBookingStatus
	sort?: string
	date?: Date | string
	stay_after?: Date
	stay_before?: Date
	overall_checkin_after?: Date
	overall_checkin_before?: Date
	overall_checkout_after?: Date
	overall_checkout_before?: Date
}

export interface IFiltersInLocation extends TSearchParams {
	booking_tags?: string[]
	booking_stages?: string[]
	payment_statuses?: string[]
	trip_sources?: string[]
	trip_owners?: string[]
	owners?: string[]
	locations?: string[]
	trip_destinations?: string[]
	sa?: string
	sb?: string
	oca?: string
	ocb?: string
	oda?: string
	odb?: string
	hotels?: string[]
	hotel_groups?: string[]
	status?: TBookingStatus
	sort?: string
	date?: string | null
}

type TAllowedWindowSizes = 1 | 3 | 5 | 7 | 10

const HOTEL_OPERATIONAL_BOOKING_CALENDAR_HAS_COMPACT_FIT_KEY = "hobc_icf"
const HOTEL_OPERATIONAL_BOOKING_CALENDAR_TRANSITION_WINDOW_SIZE_KEY = "hobc_tws"
const HOTEL_OPERATIONAL_BOOKING_CALENDAR_SHOW_RESV_TEAM_KEY = "hobc_srt"

function storeCompactFitStateToStorage(value: boolean) {
	Storage.put(
		HOTEL_OPERATIONAL_BOOKING_CALENDAR_HAS_COMPACT_FIT_KEY,
		Number(value)
	)
}
function storeResvTeamToStorage(value: boolean) {
	Storage.put(
		HOTEL_OPERATIONAL_BOOKING_CALENDAR_SHOW_RESV_TEAM_KEY,
		Number(value)
	)
}
function storeTransitionWindowSizeToStorage(value: number) {
	Storage.put(
		HOTEL_OPERATIONAL_BOOKING_CALENDAR_TRANSITION_WINDOW_SIZE_KEY,
		value
	)
}

function getHasCompactFitFromStorage(): boolean {
	return castStorageValueToBoolean(
		Storage.get(HOTEL_OPERATIONAL_BOOKING_CALENDAR_HAS_COMPACT_FIT_KEY, 1),
		true
	)
}
function getShowResvTeamFromStorage(): boolean {
	return castStorageValueToBoolean(
		Storage.get(HOTEL_OPERATIONAL_BOOKING_CALENDAR_SHOW_RESV_TEAM_KEY, 0),
		false
	)
}
function castStorageValueToBoolean(value: unknown, defaultValue = false) {
	if (["0", "1"].indexOf(value as string) === -1) {
		return defaultValue
	}
	return Boolean(Number(value))
}

export const TransitionWindowOptions: Array<TAllowedWindowSizes> = [
	1, 3, 5, 7, 10,
]

function ensureValueInAllowedOptions(value: unknown): TAllowedWindowSizes {
	if (TransitionWindowOptions.indexOf(Number(value) as never) === -1) {
		return 1
	}
	return Number(value) as TAllowedWindowSizes
}

function getTransitionWindowSizeFromStorage(): TAllowedWindowSizes {
	const size = Storage.get(
		HOTEL_OPERATIONAL_BOOKING_CALENDAR_TRANSITION_WINDOW_SIZE_KEY,
		1
	)
	return ensureValueInAllowedOptions(size)
}

export function useCalendarUIPreferences() {
	const initialPreferences = useMemo(() => {
		return {
			hasCompactFit: getHasCompactFitFromStorage(),
			transitionWindowSize: getTransitionWindowSizeFromStorage(),
			showResvTeam: getShowResvTeamFromStorage(),
		}
	}, [])
	const [calendarUIPreferences, dispatch] = useReducer<
		React.Reducer<
			typeof initialPreferences,
			| {
					type: "TOGGLE_COMPACT_FIT" | "TOGGLE_RESV_TEAM_VISIBILITY"
			  }
			| { type: "CHANGE_TRANSITION_WINDOW_SIZE"; payload: TAllowedWindowSizes }
		>
	>((state, action) => {
		switch (action.type) {
			case "TOGGLE_COMPACT_FIT":
				return {
					...state,
					hasCompactFit: !state.hasCompactFit,
				}
			case "TOGGLE_RESV_TEAM_VISIBILITY":
				return {
					...state,
					showResvTeam: !state.showResvTeam,
				}
			case "CHANGE_TRANSITION_WINDOW_SIZE":
				return {
					...state,
					transitionWindowSize: action.payload,
				}
			default:
				return state
		}
	}, initialPreferences)

	const toggleCompactFit = useCallback(() => {
		dispatch({ type: "TOGGLE_COMPACT_FIT" })
	}, [])
	const toggleResvTeamVisibility = useCallback(() => {
		dispatch({ type: "TOGGLE_RESV_TEAM_VISIBILITY" })
	}, [])
	const changeTransitionWindowSize = useCallback((value: unknown) => {
		const size = ensureValueInAllowedOptions(value)
		dispatch({
			type: "CHANGE_TRANSITION_WINDOW_SIZE",
			payload: size,
		})
	}, [])
	// store the preferences into local storage
	useEffect(() => {
		storeCompactFitStateToStorage(calendarUIPreferences.hasCompactFit)
		storeTransitionWindowSizeToStorage(
			calendarUIPreferences.transitionWindowSize
		)
		storeResvTeamToStorage(calendarUIPreferences.showResvTeam)
	}, [calendarUIPreferences])
	return {
		calendarUIPreferences,
		toggleCompactFit,
		changeTransitionWindowSize,
		toggleResvTeamVisibility,
	}
}

export function filtersToRequestParams(filters: IFilters) {
	const {
		booking_tags = [],
		trip_sources = [],
		locations = [],
		overall_checkin_after,
		overall_checkin_before,
		overall_checkout_after,
		overall_checkout_before,
		booking_stages = [],
		payment_statuses = [],
		hotels = [],
		hotel_groups = [],
		trip_owners = [],
		sort,
		trip_destinations = [],
		date,
		owners = [],
		...otherParams
	} = filters
	return {
		booking_tags: booking_tags.map((t) => t.name),
		booking_stages: booking_stages.map((t) => t.name),
		payment_statuses: payment_statuses.map((t) => t.id),
		trip_sources: trip_sources.map((t) => t.id),
		locations: locations.map((t) => t.name),
		hotels: hotels.map((t) => t.id),
		hotel_groups: hotel_groups.map((t) => t.id),
		overall_checkin_after: overall_checkin_after
			? dateToUTCString(startOf(overall_checkin_after, "day"))
			: null,

		overall_checkin_after_local: overall_checkin_after
			? formatDate(startOf(overall_checkin_after, "day"), "YYYY-MM-DD")
			: null,

		overall_checkin_before: overall_checkin_before
			? dateToUTCString(startOf(overall_checkin_before, "day"))
			: null,

		overall_checkin_before_local: overall_checkin_before
			? formatDate(startOf(overall_checkin_before, "day"), "YYYY-MM-DD")
			: null,
		overall_checkout_after: overall_checkout_after
			? dateToUTCString(startOf(overall_checkout_after, "day"))
			: null,

		overall_checkout_after_local: overall_checkout_after
			? formatDate(startOf(overall_checkout_after, "day"), "YYYY-MM-DD")
			: null,

		overall_checkout_before: overall_checkout_before
			? dateToUTCString(startOf(overall_checkout_before, "day"))
			: null,

		overall_checkout_before_local: overall_checkout_before
			? formatDate(startOf(overall_checkout_before, "day"), "YYYY-MM-DD")
			: null,
		trip_destinations: trip_destinations.map((d) => d.id),
		owners: owners.map((o) => o.id),
		trip_owners: trip_owners.map((o) => o.id),
		sort: "overall_checkin",
		...otherParams,
	}
}

function getHotelBookingsPerSlot<T extends IBooking>(
	booking: T,
	interval: Date[]
): Array<TBookingSlot<T>> {
	return interval.map((slot) => {
		const checkinDate = localOrUtcTimestampToLocalDate(
			booking.checkin_local,
			booking.checkin
		)

		const checkoutDate = localOrUtcTimestampToLocalDate(
			booking.checkout_local,
			booking.checkout
		)

		// Marks the booking start day
		const isCheckin = isSame(slot, checkinDate, "day")

		// Marks the booking checkout day
		const isCheckout = isSame(slot, checkoutDate, "day")

		/**
		 * MATCH STAY DETAILS FOR CURRENT CALENDAR SLOT
		 *
		 * Booking details are generated per occupied night.
		 *
		 * Example:
		 * Booking: 1 May → 6 May
		 *
		 * Detail dates usually exist for:
		 * 1,2,3,4,5
		 *
		 * Checkout day (6th) normally has NO stay detail
		 * because guest is no longer occupying room that night.
		 */
		const matchingDetails =
			booking.details?.filter((detail) =>
				isSame(
					localOrUtcTimestampToLocalDate(detail.date_local, detail.date),
					slot,
					"day"
				)
			) || []

		/**
		 * HANDLE CHECKOUT DAY FALLBACK
		 *
		 * Checkout day can still be visible in calendar UI
		 * even though no stay detail exists for that date.
		 *
		 * Example:
		 * Booking: 1 → 3
		 *
		 * Occupied nights:
		 * 1,2
		 *
		 * On 3rd:
		 * - checkout indicator should still render
		 * - booking tooltip/card still needs data
		 *
		 * So we fallback to the last available booking detail
		 * to preserve rendering consistency.
		 */
		const detailsForSlot =
			matchingDetails.length > 0
				? matchingDetails
				: isCheckout
					? booking.details?.slice(-1) || []
					: []

		/**
		 * Determines whether current slot belongs to booking range.
		 *
		 * Includes:
		 * - checkin day
		 * - occupied stay days
		 * - checkout day
		 *
		 * Used to decide whether booking block should render.
		 */
		const isWithinBooking =
			isSame(slot, checkinDate, "day") ||
			isSame(slot, checkoutDate, "day") ||
			(isAfter(slot, checkinDate) && isBefore(slot, checkoutDate))

		// No booking visibility for this slot
		if (!detailsForSlot.length && !isWithinBooking) {
			return {
				date: slot,
				booking: undefined,
			}
		}

		return {
			date: slot,
			booking: {
				...booking,

				details: booking.details,

				/**
				 * Used for calendar styling and UX indicators.
				 */
				day_type: isCheckin ? "checkin" : isCheckout ? "checkout" : "stay",
			},
		}
	})
}

export function arrangeHotelBookingsInCalendar<TBooking extends IBooking>(
	bookings: TBooking[],
	interval: Date[],
	compact?: boolean
): Array<Array<TBookingSlot<TBooking>>> {
	/**
	 * SORT BOOKINGS BEFORE PLACEMENT
	 *
	 * Primary sort:
	 * - earlier check-in first
	 *
	 * Secondary sort:
	 * - stable ordering using booking id
	 *
	 * This ensures deterministic row placement
	 * and prevents random visual shifts between renders.
	 */
	const sorted = [...bookings].sort((a, b) =>
		a.checkin > b.checkin ? 1 : a.checkin === b.checkin ? a.id - b.id : -1
	)
	/**
	 * Represents visual calendar rows.
	 *
	 * Each row contains slots mapped against interval dates.
	 */
	const rows: Array<Array<TBookingSlot<TBooking>>> = []

	/**
	 * Tracks next available row index.
	 */
	let currentRow = 0

	/**
	 * NON-COMPACT MODE
	 *
	 * Every booking gets its own dedicated row.
	 *
	 * Used when:
	 * - overlap visualization is preferred
	 * - row compaction is disabled
	 * - easier visual debugging is needed
	 */
	if (!compact) {
		sorted.forEach((booking) => {
			rows[currentRow] = getHotelBookingsPerSlot(booking, interval)
			currentRow++
		})

		return rows
	}

	/**
	 * COMPACT MODE
	 *
	 * Attempts to fit bookings into existing rows
	 * without overlapping occupied slots.
	 *
	 * Goal:
	 * - reduce vertical space
	 * - keep calendar dense and readable
	 */
	for (const booking of sorted) {
		/**
		 * Converts booking into interval-aligned slots.
		 */
		const slots = getHotelBookingsPerSlot(booking, interval)

		/**
		 * Finds first visible slot occupied by booking.
		 *
		 * Used as starting point for overlap checks.
		 */
		const firstIndex = slots.findIndex((s) => Boolean(s.booking))

		/**
		 * Default placement is next available row.
		 *
		 * Will try moving upward if free space exists.
		 */
		let pushToRow = currentRow

		/**
		 * Attempt upward row compaction.
		 *
		 * Checks previous rows from bottom → top
		 * to find earliest row where booking can fit
		 * without overlapping existing bookings.
		 */
		if (firstIndex !== -1 && currentRow > 0) {
			for (let j = pushToRow - 1; j >= 0; j--) {
				/**
				 * Booking can fit only if all slots from
				 * booking start onward are empty in target row.
				 */
				const canFit = rows[j].slice(firstIndex).every((s) => !s.booking)

				if (canFit) pushToRow = j
			}
		}

		/**
		 * Create new row if target row doesn't exist.
		 */
		if (!rows[pushToRow]) {
			rows[pushToRow] = slots
		} else {
			/**
			 * Merge booking into existing row.
			 *
			 * Existing bookings are preserved.
			 * Empty cells are filled with current booking slots.
			 */
			rows[pushToRow] = rows[pushToRow].map((cell, i) =>
				cell.booking ? cell : slots[i]
			)
		}

		/**
		 * If booking occupied latest row,
		 * increment row counter for future placements.
		 */
		if (pushToRow === currentRow) currentRow++
	}

	return rows
}
export function getPaxDisplay(
	adults: number,
	children?: number,
	mode: TPaxDisplayMode = "full"
): string {
	// FULL
	if (mode === "full") {
		if (!children) {
			return `${adults} ${adults === 1 ? "Adult" : "Adults"}`
		}
		return `${adults}Ad. + ${children}Ch.`
	}

	// SHORT
	if (mode === "short") {
		if (!children) return `${adults}A`
		return `${adults}A + ${children}C`
	}

	// COMPACT (with dot + multiline)
	if (!children) {
		return `${adults}A`
	}

	return `${adults}A\n•\n${children}C`
}

export const BOOKING_STAGE_META: Record<
	string,
	{ background: string; borderColor: string }
> = {
	initialized: {
		background: "#E5E7EB",
		borderColor: "#9CA3AF", // ~30% darker than #E5E7EB
	},
	in_progress: {
		background: "#C2D0FF",
		borderColor: "#6B8AFF", // noticeably darker blue-purple
	},
	booked: {
		background: "#B2DDB5",
		borderColor: "#5DB363", // noticeably darker green
	},
	default: {
		background: "#F3D673",
		borderColor: "#D4A017", // noticeably darker amber/gold
	},
}
